import { MapPin, ExternalLink } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { useLang } from "../i18n";

export default function Territorio() {
  const { data, t } = useLang();
  const { TERRITORY } = data;
  const p = t.territory;

  return (
    <>
      <Seo title={p.seo.title} description={p.seo.description} path="/il-territorio" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-12 md:pb-16">
        <Reveal>
          <p className="eyebrow mb-5">{p.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink max-w-3xl">
            {p.h1}
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-8 text-base md:text-lg text-body leading-relaxed max-w-2xl">
            {p.lead}
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <nav aria-label={p.navLabel} className="mt-10 flex flex-wrap gap-2.5">
            {TERRITORY.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                data-testid={`territory-nav-${cat.id}`}
                className="rounded-full border border-sand bg-surface px-4 py-2 text-xs tracking-wide text-sage transition-colors duration-300 hover:bg-sage hover:text-cream hover:border-sage"
              >
                {cat.title}
              </a>
            ))}
          </nav>
        </Reveal>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28 space-y-20 md:space-y-28">
        {TERRITORY.map((cat, idx) => (
          <section key={cat.id} id={cat.id} data-testid={`territory-category-${cat.id}`} className="scroll-mt-28">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-baseline border-t-2 border-sage/30 pt-8">
                <p className="md:col-span-2 font-serif text-4xl text-sand select-none" aria-hidden="true">
                  {String(idx + 1).padStart(2, "0")}
                </p>
                <h2 className="md:col-span-6 font-serif text-3xl md:text-4xl text-ink leading-snug">
                  {cat.title}
                </h2>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-6 md:ml-[16.66%] max-w-2xl text-sm md:text-base text-body leading-relaxed">
                {cat.intro}
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cat.places.map((place, i) => (
                <Reveal key={place.name} delay={0.06 * i}>
                  <article className="h-full border border-sand bg-card p-7 flex flex-col transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_44px_-26px_rgba(26,26,26,0.35)]">
                    <h3 className="font-serif text-xl md:text-2xl text-ink leading-snug">{place.name}</h3>
                    <p className="mt-3 text-sm text-body leading-relaxed flex-1">{place.why}</p>
                    <p className="mt-5 inline-flex items-center gap-2 text-xs text-sage">
                      <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                      {place.distance}
                    </p>
                    <a
                      href={place.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`territory-link-${cat.id}-${i}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors duration-300"
                    >
                      {place.linkLabel} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 pb-10">
        <p className="text-xs text-body/70 leading-relaxed max-w-3xl">
          {p.credits}
        </p>
      </div>

      <CTABand title={p.ctaTitle} text={p.ctaText} />
    </>
  );
}
